import { z } from 'zod';
import {
  CALL_OUT_OF_JURISDICTION,
  CALL_REROUTED,
  RTI_DOWNSTREAM_SHARE,
} from '@rsos/constants/collaborator';
import { captureExceptionWithScope } from '@rsos/utils/sentry';

/**
 * Mirrors rtt_egress's CollaboratorBodySchema (schemas.py). Collaborator and
 * rti_downstream events share this one envelope on the backend -- the two
 * field groups below (the jurisdictional-, receiving-, and
 * collaborator-conversation-id fields, vs. the primary- and
 * rti-downstream-conversation-id fields) are mutually exclusive per call,
 * which rtt_ingress enforces upstream, not this schema.
 *
 * `.passthrough()` because this module only cares about the fields it
 * normalizes below -- location/extra_info/etc fields also present on the
 * envelope are intentionally left unvalidated here.
 */
export const collaboratorBodySchema = z
  .object({
    caller_id: z.string(),
    // Unset fields arrive as explicit `null`, not an absent key
    call_end_time: z.number().nullable().optional(),
    jurisdictional_account_id: z.string().nullable().optional(),
    jurisdictional_display_name: z.string().nullable().optional(),
    receiving_account_id: z.string().nullable().optional(),
    receiving_display_name: z.string().nullable().optional(),
    dispatcher_position: z.string().nullable().optional(),
    collaborator_conversation_id: z.string().nullable().optional(),
    primary_account_id: z.string().nullable().optional(),
    primary_display_name: z.string().nullable().optional(),
    // Nested SpeakerSchema on the backend -- an object, not a string, unlike
    // the plain-string dispatcher_position above.
    primary_dispatcher_position: z
      .object({
        id: z.string().optional(),
        name: z.string().optional(),
        role: z.string().optional(),
        display_name: z.string().optional(),
        language_detected: z.boolean().optional(),
      })
      .nullable()
      .optional(),
    rti_downstream_conversation_id: z.string().nullable().optional(),
    downstream_agencies: z
      .array(z.object({ account_id: z.string(), display_name: z.string() }))
      .nullable()
      .optional(),
  })
  .passthrough();

export const collaboratorMessageTypeSchema = z.enum([
  CALL_REROUTED,
  CALL_OUT_OF_JURISDICTION,
  RTI_DOWNSTREAM_SHARE,
]);

export type CollaboratorBody = z.infer<typeof collaboratorBodySchema>;
export type CollaboratorMessageType = z.infer<
  typeof collaboratorMessageTypeSchema
>;

export interface CollaboratorPayload {
  conversationID: string | null;
  type: CollaboratorMessageType;
  pairedAccountID: string | null;
  pairedAccountName: string | null;
  isUnread: boolean;
  dispatcherPosition: string | null;
  callEnded: boolean;
  downstreamAgencies?: { accountID: string; displayName: string }[];
}

/**
 * Validates and normalizes a collaborator/rti_downstream websocket or REST
 * payload into the shared `collaborator` queue-entry shape. Returns `null`
 * (logging to Sentry) instead of throwing when `type`/`body` don't match the
 * backend contract, so a malformed payload can't crash the queue -- callers
 * should treat `null` as "don't update the collaborator for this call".
 */
export function buildCollaboratorPayload(
  type: unknown,
  body: unknown,
): CollaboratorPayload | null {
  const parsedType = collaboratorMessageTypeSchema.safeParse(type);
  const parsedBody = collaboratorBodySchema.safeParse(body);

  if (!parsedType.success || !parsedBody.success) {
    captureExceptionWithScope(new Error('Invalid collaborator payload'), {
      type,
      body,
      typeError: parsedType.success ? null : parsedType.error.flatten(),
      bodyError: parsedBody.success ? null : parsedBody.error.flatten(),
    });
    return null;
  }

  const messageType = parsedType.data;
  const collaboratorBody = parsedBody.data;
  const callEnded = !!(
    collaboratorBody.call_end_time && collaboratorBody.call_end_time > 0
  );

  if (messageType === RTI_DOWNSTREAM_SHARE) {
    return {
      conversationID: collaboratorBody.rti_downstream_conversation_id ?? null,
      type: messageType,
      pairedAccountID: collaboratorBody.primary_account_id ?? null,
      pairedAccountName: collaboratorBody.primary_display_name ?? null,
      isUnread: false,
      dispatcherPosition:
        collaboratorBody.primary_dispatcher_position?.display_name ?? null,
      callEnded,
      downstreamAgencies: collaboratorBody.downstream_agencies?.map(agency => ({
        accountID: agency.account_id,
        displayName: agency.display_name,
      })),
    };
  }

  return {
    conversationID: collaboratorBody.collaborator_conversation_id ?? null,
    type: messageType,
    pairedAccountID:
      collaboratorBody.jurisdictional_account_id ||
      collaboratorBody.receiving_account_id ||
      null,
    pairedAccountName:
      collaboratorBody.jurisdictional_display_name ||
      collaboratorBody.receiving_display_name ||
      null,
    isUnread: false,
    dispatcherPosition: collaboratorBody.dispatcher_position ?? null,
    callEnded,
  };
}
